import React, { useState, useEffect, useMemo } from "react";
import BonusConfirmPopup from "./BonusConfirmPopup";
import RulesPopup from "./RulesPopup";
import { motion } from "framer-motion";
import config from "../config";
import { useStatic } from "../context/StaticContext";
import { useTranslation } from "../hooks/useTranslation";
import "../css/CleanMaintenanceMode.css";

const BonusCard = ({
  title,
  processName,
  imagePath,
  isMaintenance,
  rules,
  isMultiple,
  multipleOptions,
  redirectLink,
  confirmText,
  onMultipleBonusRequest,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [selectedBonusFromMultiple, setSelectedBonusFromMultiple] = useState(null);
  const { selectedBonus } = useStatic();
  const { t } = useTranslation();

  const handleRequestBonus = () => {
    if (isMaintenance) return; // Bakım modundaysa işlemi engelle

    // Çoklu bonus kontrolü
    if (isMultiple && multipleOptions) {
      onMultipleBonusRequest(multipleOptions);
      return;
    }

    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setSelectedBonusFromMultiple(null); // Seçili bonusu temizle
  };

  const handleConfirmBonus = () => {
    // Burada bonus talebi onaylandığında yapılacak işlemler olacak
    const bonusTitle = selectedBonusFromMultiple
      ? selectedBonusFromMultiple.bonus_name
      : title;
    alert(`${bonusTitle} talebi başarıyla onaylandı!`);
    setIsConfirmOpen(false);
    setSelectedBonusFromMultiple(null); // Seçili bonusu temizle
  };

  const handleShowRules = () => {
    setIsRulesOpen(true);
  };

  const handleCloseRules = () => {
    setIsRulesOpen(false);
  };

  useEffect(() => {
    if (selectedBonus && selectedBonus.bonus && isMultiple && multipleOptions) {
      const isThisCard = multipleOptions.some(
        (option) => option.process_name === selectedBonus.bonus.process_name
      );

      if (isThisCard) {
        setSelectedBonusFromMultiple(selectedBonus.bonus);
        setIsConfirmOpen(true);
      }
    }
  }, [isMultiple, multipleOptions, selectedBonus]);

  const imageUrl = useMemo(() => {
    return imagePath && imagePath.startsWith("/")
      ? `${process.env.PUBLIC_URL}${imagePath}`
      : imagePath;
  }, [imagePath]);

  const bonusData = useMemo(() => {
    return selectedBonusFromMultiple
      ? {
          title: selectedBonusFromMultiple.bonus_name,
          process_name: selectedBonusFromMultiple.process_name,
          rules: selectedBonusFromMultiple.kurallar || rules,
          image: `${config.imgUrl}${imageUrl}`,
          redirect_link: selectedBonusFromMultiple.redirect_link || null,
          talep_text: selectedBonusFromMultiple.talep_text || confirmText || null,
        }
      : {
          title: title,
          process_name: processName,
          rules: rules,
          image: `${config.imgUrl}${imageUrl}`,
          redirect_link: redirectLink || null,
          talep_text: confirmText || null,
        };
  }, [selectedBonusFromMultiple, title, processName, rules, imageUrl, redirectLink]);

  // Maintenance ikonu SVG'si
  const MaintenanceIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <>
      <motion.div
        className={`bonus-card-container ${isMaintenance ? "clean-maintenance-mode" : ""}`}
      >
        <div className="bonus-card">
          <img src={`${config.imgUrl}${imageUrl}`} alt={title} className="card-image" />

          {/* Hover efektinde görünecek bulanık arka plan ve butonlar */}
          <div className="hover-overlay">
            <div className="button-container">
              <button
                className={`btn-action ${isMaintenance ? "disabled" : ""}`}
                onClick={handleRequestBonus}
                disabled={isMaintenance}
              >
                {t('buttons.request', 'Talep Et')}
              </button>
              <button className="btn-rules" onClick={handleShowRules}>
                {t('buttons.rules', 'Kurallar')}
              </button>
            </div>
          </div>

          {/* Yeni temiz maintenance overlay */}
          {isMaintenance && (
            <div className="clean-maintenance-overlay">
              <div className="clean-maintenance-icon">
                <MaintenanceIcon />
              </div>
              <p className="clean-maintenance-text">
                {t('maintenance.title', 'Bakımda')}
              </p>
              <p className="clean-maintenance-subtitle">
                {t('maintenance.subtitle', 'Yakında aktif olacak')}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      <BonusConfirmPopup
        isOpen={isConfirmOpen}
        onClose={handleCloseConfirm}
        bonus={bonusData}
        onConfirm={handleConfirmBonus}
      />

      <RulesPopup
        isOpen={isRulesOpen}
        onClose={handleCloseRules}
        bonusTitle={title}
        rules={rules}
      />
    </>
  );
};

export default BonusCard;
